import template from './startup-overlay.template.html?raw';
import { STARTUP_OVERLAY_CONFIG } from './config';

type StartupOverlayOptions = {
  onRevealStart?: () => void;
  onComplete: () => void;
};

const MAIN_TEXTS = [
  'Connecting to server...',
  'Syncing game state...',
  'Loading players...',
  'Fueling the rocket...',
  'Ready to launch!'
] as const;

const SUB_TEXTS = [
  'Checking diamond hands...',
  'Syncing market signals...',
  'Bringing rounds online...',
  'Heating launch engines...',
  'Line goes up'
] as const;

export class StartupOverlay {
  private readonly root: HTMLElement | null;
  private readonly fillEl: HTMLElement | null;
  private readonly progressEl: HTMLElement | null;
  private readonly mainEl: HTMLElement | null;
  private readonly subEl: HTMLElement | null;
  private readonly starsHost: HTMLElement | null;
  private readonly onRevealStart: (() => void) | null;
  private readonly onComplete: () => void;

  private progress = 0;
  private backendReady = false;
  private active = false;
  private finished = false;
  private startedAt = 0;
  private finalStageStartedAt = 0;
  private backendReadyBeforeNinetyNine = false;
  private revealStarted = false;
  private stepTimer: number | null = null;
  private textTimer: number | null = null;

  constructor(options: StartupOverlayOptions) {
    this.root = document.getElementById('startup-overlay');
    this.onRevealStart = options.onRevealStart ?? null;
    this.onComplete = options.onComplete;

    if (this.root) {
      this.root.innerHTML = template;
    }

    this.fillEl = this.root?.querySelector<HTMLElement>('[data-startup-fill]') ?? null;
    this.progressEl = this.root?.querySelector<HTMLElement>('[data-startup-progress]') ?? null;
    this.mainEl = this.root?.querySelector<HTMLElement>('[data-startup-main]') ?? null;
    this.subEl = this.root?.querySelector<HTMLElement>('[data-startup-sub]') ?? null;
    this.starsHost = this.root?.querySelector<HTMLElement>('[data-startup-stars]') ?? null;

    this.initStars();
  }

  start(): void {
    if (!this.root || this.active || this.finished) {
      return;
    }

    this.active = true;
    this.startedAt = Date.now();
    this.finalStageStartedAt = 0;
    this.backendReadyBeforeNinetyNine = false;
    this.revealStarted = false;
    this.root.classList.add('is-active');
    this.root.setAttribute('aria-busy', 'true');
    this.root.setAttribute('aria-hidden', 'false');

    this.textTimer = window.setInterval(() => {
      if (!this.subEl || this.backendReady) {
        return;
      }
      const randomText = SUB_TEXTS[Math.floor(Math.random() * SUB_TEXTS.length)];
      this.subEl.textContent = randomText;
    }, STARTUP_OVERLAY_CONFIG.statusRotateMs);

    this.stepTimer = window.setInterval(() => {
      this.stepProgress();
    }, STARTUP_OVERLAY_CONFIG.progressTickMs);

    this.render();
  }

  markBackendReady(): void {
    if (this.backendReady || this.finished) {
      return;
    }
    this.backendReadyBeforeNinetyNine = this.progress < 99;
    this.backendReady = true;
    if (this.mainEl) {
      this.mainEl.textContent = MAIN_TEXTS[MAIN_TEXTS.length - 1];
    }
    if (this.subEl) {
      this.subEl.textContent = 'Systems online';
    }
  }

  private initStars(): void {
    if (!this.starsHost) {
      return;
    }

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < 40; i += 1) {
      const star = document.createElement('span');
      star.className = 'startup-overlay__star';
      const size = Math.random() * 3 + 1;
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.setProperty('--star-dur', `${Math.random() * 2 + 1}s`);
      fragment.appendChild(star);
    }
    this.starsHost.appendChild(fragment);
  }

  private stepProgress(): void {
    if (!this.active || this.finished) {
      return;
    }

    const before = this.progress;
    const now = Date.now();
    const progressBarMs = STARTUP_OVERLAY_CONFIG.progressBarSeconds * 1000;
    const perTickDelta = (100 * STARTUP_OVERLAY_CONFIG.progressTickMs) / progressBarMs;

    if (!this.backendReady) {
      const elapsed = Math.max(0, now - this.startedAt);
      const target = Math.min(99, (elapsed / progressBarMs) * 99);
      this.progress = this.advanceTowards(target, perTickDelta);
    } else {
      if (this.backendReadyBeforeNinetyNine) {
        const elapsed = Math.max(0, now - this.startedAt);
        const target = Math.min(100, (elapsed / progressBarMs) * 100);
        this.progress = this.advanceTowards(target, perTickDelta);
      } else {
        this.progress = this.advanceTowards(100, perTickDelta);
      }
    }

    if (Math.floor(this.progress / 20) !== Math.floor(before / 20) && this.mainEl) {
      const stage = Math.min(
        MAIN_TEXTS.length - 2,
        Math.floor((this.progress / 100) * (MAIN_TEXTS.length - 1))
      );
      this.mainEl.textContent = MAIN_TEXTS[stage];
    }

    this.render();

    if (this.backendReady && this.progress >= 100) {
      this.finish();
    }
  }

  private render(): void {
    if (this.fillEl) {
      this.fillEl.style.width = `${this.progress.toFixed(2)}%`;
    }
    if (this.progressEl) {
      this.progressEl.textContent = Math.round(this.progress).toString();
    }
  }

  private finish(): void {
    if (!this.root || this.finished) {
      return;
    }

    this.finished = true;
    this.active = false;

    if (this.stepTimer !== null) {
      window.clearInterval(this.stepTimer);
      this.stepTimer = null;
    }

    if (this.textTimer !== null) {
      window.clearInterval(this.textTimer);
      this.textTimer = null;
    }

    this.startRevealIfNeeded();
    if (this.root) {
      this.root.setAttribute('aria-hidden', 'true');
      this.root.style.display = 'none';
    }
    this.onComplete();
  }

  private startRevealIfNeeded(): void {
    if (this.revealStarted) {
      return;
    }
    this.revealStarted = true;
    this.onRevealStart?.();
  }

  private advanceTowards(target: number, maxDelta: number): number {
    if (target <= this.progress) {
      return this.progress;
    }
    return Math.min(target, this.progress + maxDelta);
  }
}
